// https://www.figma.com/design/8b6QnKR8kkpkjjDLYRdMNO/-External-_Parkd_Brand_Web?node-id=381-6428&t=0HeOxyVzYSfbnBcV-0

import { FormRegistrationData, getValidationSchema } from './validationSchemas';
import { ChangeEvent, Dispatch, FormEvent, SetStateAction, useEffect, useState } from 'react';
import { formatMessage } from 'i18n/CurrentLocale';
import { FormattedMessage, useIntl } from 'react-intl';
import { CompanyDataFromVAT } from 'types';
import ReactFlagsSelect from 'react-flags-select';
import placeholders from 'string-translations/placeholders';
import { getTranslatedSupportedCountryLabels, stripStringFromNonAlphaNumericCharacters } from 'myParkdUtils/utils';
import { Api } from 'api';
import { ValidationError } from './ValidationError';
import { RegistrationFlowType } from 'myParkdUtils/enums';
import { useDebounce, useSkipFirstEffect } from 'myParkdUtils/customHooks';
import { TouchedFields, TouchedFieldsCompany } from './utils';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { FormInvalidMessage } from './FormInvalidMessage';
import * as Sentry from '@sentry/react';
import { Schema } from 'yup';
import { InputSector, Sector } from './InputSector';
import { countryCodes } from './RegistrationScreen';
import { GAAction, trackDataLayerEvent } from 'myParkdUtils/googleAnalytics';


const initialTouchedFields: TouchedFieldsCompany = {
    vat: false,
    companyName: false,
    addressStreet: false,
    addressNumber: false,
    addressCity: false,
    addressPostalCode: false,
    addressCountry: false,
    invoicingEmailAddress: false,
    sector: false
}

export const StepCompanyDetails = (props: Props) => {
    const intl = useIntl();

    const { flowType, formData, touched, errors, getClassNames, setTouched, setErrors,
        handleUserInput, setFormData, setFormStep, handleBlur, prefillCompanyFieldsBasedOnVAT } = props;

    const [loadingVAT, setLoadingVAT] = useState<boolean>(false);
    const [vatApiErrorStatus, setVATApiErrorStatus] = useState<Number>();
    const [showValidation, setShowValidation] = useState<boolean>(false);

    const debouncedVat = useDebounce(formData.vat, 500);
    const validationSchema = getValidationSchema(intl, 'STEP_COMPANY');

    useEffect(() => {
        (async () => {
            const { errors } = await props.validateForm(validationSchema, { vatApiErrorStatus });
            setErrors(errors);
        })();
    }, [formData, vatApiErrorStatus])


    useSkipFirstEffect(() => {
        (async () => {
            await validationSchema.validateAt('vat', { vat: debouncedVat })
                .then(async () => {
                    try {
                        setLoadingVAT(true);
                        const companyData = await Api.getCompanyDetailsByVAT(debouncedVat);
                        setVATApiErrorStatus(undefined);
                        await prefillCompanyFieldsBasedOnVAT(companyData);
                    } catch (err: any) {
                        if (err?.status === 400 && err?.response?.body?.data?.errorKey === 'INVALID_VAT') {
                            setVATApiErrorStatus(400);
                        } else {
                            setVATApiErrorStatus(500);
                            Sentry.captureException(err);
                        }
                    } finally {
                        setLoadingVAT(false);
                    }
                }).catch((e) => {
                    console.debug('YUP: invalid VAT format');
                });
        })();
    }, [debouncedVat]);


    const onChangeVAT = async (vat: string) => {
        const sanitizedVAT = stripStringFromNonAlphaNumericCharacters(vat).toUpperCase();
        setFormData((prevState) => ({
            ...prevState,
            vat: sanitizedVAT
        }))
    }

    const onSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setShowValidation(true);
        const { isValid } = await props.validateForm(validationSchema, { vatApiErrorStatus });
        if (isValid) {
            trackDataLayerEvent({ event: GAAction.REGISTRATION_SINGLE_STEP_COMPLETED, registrationStep: 'step_company' });
            setFormStep(2);
        }
    }

    return (
        <div>
            <div className='mb-1'>
                <h1 className="title-black"><FormattedMessage id="REGISTRATION_COMPANY_DETAILS_TITLE" /></h1>
                <p><FormattedMessage id="REGISTRATION_COMPANY_DETAILS_SUBTITLE" /></p>
            </div>

            <form onSubmit={onSubmit} id='form-step-company' className={`full-width-form ${showValidation ? 'show-validation-messages' : ''}`}>
                <fieldset className='registration-group'>

                    <h3 className='bold-title'>
                        <FormattedMessage
                            id={flowType === 'TRACK_AND_TRACE'
                                ? 'REGISTRATION_TITLE_COMPANY_TRACK_AND_TRACE'
                                : 'REGISTRATION_TITLE_COMPANY'}
                        />
                    </h3>

                    <div className="grid-2-col-gap">
                        <div className="col">
                            <label htmlFor="regVatNumber"><FormattedMessage id="REGISTRATION_VAT_NUMBER" /></label>
                            <div className="rel">
                                <input type="text" id="regVatNumber" name='vat' aria-label='vat'
                                    className={`text-field ${getClassNames('vat')}`} aria-invalid={!!errors?.vat}
                                    value={formData?.vat}
                                    onChange={(e) => onChangeVAT(e.target.value)}
                                    onBlur={handleBlur}
                                    placeholder={formatMessage({ id: 'PLACEHOLDER_VAT_NUMBER' })}
                                    autoComplete="vat"
                                />
                                {loadingVAT && (
                                    <div id="circle-spinner-vat" className="circle-spinner" />
                                )}
                            </div>

                            <ValidationError fieldName='vat' errors={errors} showValidation={showValidation} />
                        </div>

                        <div className='col'>
                            <label htmlFor="regCompanyName"><FormattedMessage id="REGISTRATION_COMPANY_NAME" /></label>
                            <input type="text" id="regCompanyName" name='companyName' aria-label='companyName'
                                className={`text-field ${getClassNames('companyName')}`}
                                value={formData?.companyName}
                                onChange={(e) => handleUserInput(e)}
                                onBlur={handleBlur}
                                placeholder={formatMessage({ id: 'PLACEHOLDER_COMPANY_NAME' })}
                                autoComplete="organization"
                            />
                            <ValidationError fieldName='companyName' errors={errors} showValidation={showValidation} />
                        </div>

                        <div className="col">
                            <label htmlFor="regAddressStreet"><FormattedMessage id="REGISTRATION_ADDRESS_STREET" /></label>
                            <input type="text" id="regAddressStreet" name="addressStreet" aria-label='addressStreet'
                                className={`text-field ${getClassNames('addressStreet')}`}
                                placeholder={formatMessage({ id: 'PLACEHOLDER_STREET' })}
                                autoComplete="street-address"
                                value={formData?.addressStreet}
                                onChange={(e) => handleUserInput(e)}
                                onBlur={handleBlur}
                            />
                            <ValidationError fieldName='addressStreet' errors={errors} showValidation={showValidation} />
                        </div>

                        <div className='col'>
                            <label htmlFor="regAddressNumber"><FormattedMessage id="REGISTRATION_ADDRESS_NUMBER" /></label>
                            <input type="text" id="regAddressNumber" name='addressNumber' aria-label='addressNumber'
                                className={`text-field ${getClassNames('addressNumber')}`}
                                placeholder={formatMessage({ id: 'PLACEHOLDER_HOUSE_NUMBER' })}
                                value={formData?.addressNumber}
                                onChange={(e) => handleUserInput(e)}
                                onBlur={handleBlur}
                            />
                            <ValidationError fieldName='addressNumber' errors={errors} showValidation={showValidation} />
                        </div>

                        <div className="col">
                            <label htmlFor="regAddressCity"><FormattedMessage id="REGISTRATION_ADDRESS_CITY" /></label>
                            <input type="text" id="regAddressCity" name='addressCity' aria-label='addressCity'
                                className={`text-field ${getClassNames('addressCity')}`}
                                placeholder={formatMessage({ id: 'PLACEHOLDER_CITY' })}
                                autoComplete="address-level2"
                                value={formData?.addressCity}
                                onChange={(e) => handleUserInput(e)}
                                onBlur={handleBlur}
                            />
                            <ValidationError fieldName='addressCity' errors={errors} showValidation={showValidation} />
                        </div>

                        <div className='col'>
                            <label htmlFor="regAddressPostalCode"><FormattedMessage id="REGISTRATION_ADDRESS_POSTAL_CODE" /></label>
                            <input type="text" id="regAddressPostalCode" name="addressPostalCode" aria-label='addressPostalCode'
                                className={`text-field ${getClassNames('addressPostalCode')}`}
                                placeholder={formatMessage({ id: 'PLACEHOLDER_POSTAL_CODE' })}
                                autoComplete="postal-code"
                                value={formData?.addressPostalCode}
                                onChange={(e) => handleUserInput(e)}
                                onBlur={handleBlur}
                            />
                            <ValidationError fieldName='addressPostalCode' errors={errors} showValidation={showValidation} />
                        </div>

                        <div className="col">
                            <label htmlFor="regAddressCountry"><FormattedMessage id="REGISTRATION_ADDRESS_COUNTRY" /></label>
                            <ReactFlagsSelect
                                rfsKey='rfs-address-country'
                                showSelectedLabel={true}
                                searchable={true}
                                searchPlaceholder={formatMessage(placeholders.search)}
                                selected={formData.addressCountry}
                                className="country-flags react-flags-select-inside-text-field text-field"
                                selectButtonClassName={"react-flags-select-button"}
                                onSelect={(countryCode) => {
                                    setFormData((prevState) => ({ ...prevState, addressCountry: countryCode }))
                                    handleBlur({ target: { name: 'addressCountry' } } as any);
                                }}
                                countries={countryCodes} // TODO: use customLabels for translations?
                                //customLabels={{NL: formatMessage({id: 'BELGIUM'})}}
                                customLabels={getTranslatedSupportedCountryLabels()}
                                //selectedSize={18}
                                optionsSize={18}
                            />
                        </div>

                        <div className='col'>
                            <label htmlFor="regInvoicingEmailAddress"><FormattedMessage id="REGISTRATION_INVOICING_EMAIL_ADDRESS" /></label>
                            <input type="text" id="regInvoicingEmailAddress" name="invoicingEmailAddress" aria-label='invoicingEmailAddress'
                                className={`text-field ${getClassNames('invoicingEmailAddress')}`}
                                placeholder={formatMessage({ id: 'PLACEHOLDER_EMAIL' })}
                                autoComplete="email"
                                value={formData?.invoicingEmailAddress}
                                onChange={(e) => handleUserInput(e)}
                                onBlur={handleBlur}
                            />
                            <ValidationError fieldName='invoicingEmailAddress' errors={errors} showValidation={showValidation} />
                        </div>

                        <div className='col'>
                            <label htmlFor="reg-sector"><FormattedMessage id="REGISTRATION_INDUSTRY" /></label>
                            <InputSector
                                sector={formData.sector}
                                onChange={(sector: Sector) => setFormData((prevState) => ({ ...prevState, sector }))}
                            />
                            <ValidationError fieldName='sector' errors={errors} showValidation={showValidation} />
                        </div>
                    </div>

                    <FormInvalidMessage errors={errors} showValidation={showValidation} />

                    <div className='flex-space-between mt-2'>
                        <button type="button" className="btn-back" onClick={() => {
                            window.location.href = "https://parkd.com";
                        }}>
                            <FontAwesomeIcon icon={faArrowLeft} size='xs' />
                            <span><FormattedMessage id="TO_PARKD_COM" /></span>
                        </button>
                        <button type="submit" className="btn-new">
                            <FormattedMessage id="GLOBAL_CTA_CONTINUE" />
                            <FontAwesomeIcon icon={faArrowRight} size='xs' />
                        </button>
                    </div>

                </fieldset>
            </form>
        </div>
    )
}

type Props = {
    formData: FormRegistrationData,
    flowType: RegistrationFlowType,
    touched: TouchedFields,
    errors: FormRegistrationData,
    setTouched: Dispatch<SetStateAction<TouchedFields>>,
    setErrors: Dispatch<SetStateAction<FormRegistrationData>>,
    setFormData: Dispatch<SetStateAction<FormRegistrationData>>,
    setFormStep: Dispatch<SetStateAction<number>>,
    handleUserInput: (e: ChangeEvent<HTMLInputElement>) => void,
    handleBlur: React.FocusEventHandler<HTMLInputElement>,
    getClassNames: Function,
    prefillCompanyFieldsBasedOnVAT: (companyData: CompanyDataFromVAT) => Promise<void>,
    validateForm: (schema: Schema<any>, context: any) => Promise<{ isValid: boolean, errors: any }>
}
